import React, { useEffect, useRef, useState } from "react";
import { Button, Icon } from "./StudioControls";
import { exportPDF, printDocument, vectorExportCheck, chooseSaveLocation, canChooseSaveLocation, fileNameWithExtension } from "./studioExport";
import StudioCanvas from "./StudioCanvas";
import { EXPORT_PRESETS, exportPages, changedToday } from "./studioExportPages";
export function Modal({ title, onClose, children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`ms-modal ${className}`}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          const r = e.currentTarget.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            onClose();
        }
      }}
    >
      <header>
        <h2>{title}</h2>
        <Button icon="close" title="Close dialog" onClick={onClose} />
      </header>
      {children}
    </dialog>
  );
}
export function ExportDialog({ doc, content, onClose }) {
  const [preset, setPreset] = useState("all");
  const [selected, setSelected] = useState(() => doc.pages.map((p) => p.id));
  const [busy, setBusy] = useState(false);
  const [format, setFormat] = useState("image");
  const [fileName, setFileName] = useState(() => fileNameWithExtension(doc.name, ".pdf"));
  const [error, setError] = useState("");
  const pages = doc.pages.filter((p) => selected.includes(p.id));
  const choosePreset = (key) => {
    setPreset(key);
    setSelected(exportPages(doc, key, new Date(), content).map((p) => p.id));
  };
  const toggle = (id) => {
    setPreset("custom");
    setSelected((ids) => ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]);
  };
  const run = async () => {
    if (!pages.length || busy) return;
    setBusy(true);
    setError("");
    const name = fileNameWithExtension(fileName, ".pdf");
    try {
      if (format === "vector") await printDocument(doc, pages, content, { fileName: name });
      else {
        // Ask where to save first: browsers only allow this straight from the click.
        const handle = await chooseSaveLocation(name, "application/pdf");
        if (handle === null) return;
        await exportPDF(doc, pages, content, { fileName: name, handle });
      }
    }
    catch (err) { setError(err.message || "Could not prepare your PDF. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Export PDF" onClose={onClose} className="ms-pdf-modal">
      <div className="ms-pdf-layout">
        <nav className="ms-pdf-presets" aria-label="Page presets">
          <span className="ms-kicker">EXPORT PAGES</span>
          {EXPORT_PRESETS.map(([key, label]) => (
            <button key={key} aria-pressed={preset === key} disabled={busy}
              className={preset === key ? "is-active" : ""} onClick={() => choosePreset(key)}>
              <span>{label}</span><small>{exportPages(doc, key, new Date(), content).length}</small>
            </button>
          ))}
          <button aria-pressed={preset === "custom"} disabled={busy}
            className={preset === "custom" ? "is-active" : ""} onClick={() => setPreset("custom")}>
            <span>Choose pages</span><Icon name="pages" size={15} />
          </button>
          <p className="ms-hint">Dinner includes the cover. Page order follows your studio.</p>
          <p className="ms-hint">Changed today uses your local date. Tracking starts with this update.</p>
        </nav>
        <section className="ms-pdf-selection" aria-label="Pages to export">
          <div className="ms-pdf-selection-heading">
            <span>{pages.length} of {doc.pages.length} pages selected</span>
            <button disabled={busy} onClick={() => {
              setPreset("custom");
              setSelected(pages.length === doc.pages.length ? [] : doc.pages.map((p) => p.id));
            }}>{pages.length === doc.pages.length ? "Clear selection" : "Select all"}</button>
          </div>
          {preset === "today" && !pages.length && <p className="ms-pdf-empty" role="status">No tracked changes today. Select any page below to include it.</p>}
          {preset !== "today" && preset !== "custom" && !pages.length && <p className="ms-pdf-empty" role="status">This document has no pages in that menu. Select pages below to export.</p>}
          <div className="ms-pdf-pages">
            {doc.pages.map((p, index) => {
              const checked = selected.includes(p.id);
              return <button key={p.id} disabled={busy} className={`ms-pdf-page ${checked ? "is-active" : ""}`}
                aria-pressed={checked} aria-label={`Page ${index + 1}: ${p.name}`} onClick={() => toggle(p.id)}>
                <span className="ms-pdf-page-label"><small>{String(index + 1).padStart(2, "0")}</small><span>{p.name}</span>
                  <span className="ms-pdf-checkbox">{checked && <Icon name="check" size={12} />}</span>
                </span>
                <span className="ms-pdf-preview"><StudioCanvas doc={doc} page={p} content={content}
                  zoom={Math.min(154 / p.width, 194 / p.height)} preview /></span>
                <span className="ms-pdf-page-meta">{changedToday(p, new Date(), content) ? "Edited today" : `${(p.width / 72).toFixed(2)} × ${(p.height / 72).toFixed(2)} in`}</span>
              </button>;
            })}
          </div>
        </section>
      </div>
      <footer>
        <div><span className="ms-pdf-total">{pages.length} {pages.length === 1 ? "page" : "pages"} · PDF</span>
          <label className="ms-field ms-file-name">
            <span>File name</span>
            <input value={fileName} onChange={(e) => setFileName(e.target.value)} onBlur={() => setFileName((v) => fileNameWithExtension(v, ".pdf"))} aria-label="File name" />
          </label>
          <p className="ms-hint">{format === "vector"
            ? "Opens the print dialog; choose Save as PDF with margins off. The file name above is suggested there."
            : canChooseSaveLocation()
            ? "You’ll choose where to save it. Exports at the menu’s exact page size."
            : "Saves to your browser’s download location (set your browser to ask where to save to choose a folder)."}</p>
          {(() => {
            const check = vectorExportCheck(doc, pages);
            return <div className="ms-segmented" role="group" aria-label="PDF type">
              <Button active={format === "image"} aria-pressed={format === "image"} onClick={() => setFormat("image")}>Print image (300 dpi)</Button>
              <Button active={format === "vector"} aria-pressed={format === "vector"} disabled={!check.ok} onClick={() => setFormat("vector")}>Vector</Button>
              {!check.ok && <p className="ms-hint">Vector PDF is unavailable: {check.issues.join(" ")}</p>}
            </div>;
          })()}
          {error && <p role="alert" className="ms-error">{error}</p>}
        </div>
        <Button icon="export" className="ms-primary" disabled={busy || !pages.length} onClick={run}>
          {busy ? "Preparing PDF…" : "Export PDF"}
        </Button>
      </footer>
    </Modal>
  );
}
export function ShortcutsDialog({ onClose }) {
  return (
    <Modal title="A few studio shortcuts" onClose={onClose}>
      <div className="ms-modal-body ms-shortcuts">
        {[
          ["V / H", "Selection / hand tool"],
          ["Space + drag", "Pan the workspace"],
          ["Shift + click", "Add or remove from selection"],
          ["⌘ / Ctrl + click", "Select one element inside a group"],
          ["Arrow keys", "Nudge by 1 point"],
          ["Shift + arrows", "Nudge by 10 points"],
          ["⌘ / Ctrl + Z", "Undo"],
          ["⌘ / Ctrl + Shift + Z", "Redo"],
          ["⌘ / Ctrl + C / V", "Copy / paste elements"],
          ["⌘ / Ctrl + D", "Duplicate selection"],
          ["⌘ / Ctrl + G", "Group selection"],
          ["⌘ / Ctrl + Shift + G", "Ungroup selection"],
          ["⌘ / Ctrl + A", "Select all unlocked layers"],
          ["⌘ / Ctrl + S", "Save design"],
          ["Delete / Backspace", "Delete unlocked selection"],
          ["Escape", "Clear selection"],
          ["Shift + rotate", "Snap rotation to 15°"],
          ["Alt + drag", "Temporarily disable snapping"],
        ].map(([keys, text]) => (
          <div key={keys}>
            <span>{text}</span>
            <kbd>{keys}</kbd>
          </div>
        ))}
      </div>
    </Modal>
  );
}
